"use client"

import { Cookie } from "lucide-react"
import { useState, useEffect } from "react"
import {
  CookieConsent,
  defaultConsent,
  getConsent,
  clearNonEssentialCookies,
} from "@/lib/cookies"
import { useCookies } from "@/lib/context/cookie-context"
import { Button, Label, Switch } from "@medusajs/ui"
import * as Dialog from "@radix-ui/react-dialog"

const CookieConsentBanner = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showBanner, setShowBanner] = useState(false)
  const [consentSettings, setConsentSettings] =
    useState<CookieConsent>(defaultConsent)
  const { updateConsent } = useCookies()

  useEffect(() => {
    const savedConsent = getConsent()
    if (!savedConsent.consentDate) {
      setShowBanner(true)
    }
    setConsentSettings(savedConsent)
  }, [])

  const handleAcceptAll = () => {
    const fullConsent: CookieConsent = {
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
    }
    updateConsent(fullConsent)
    setConsentSettings(fullConsent)
    setShowBanner(false)
  }

  const handleRejectAll = () => {
    updateConsent(defaultConsent)
    setConsentSettings(defaultConsent)
    setShowBanner(false)
    clearNonEssentialCookies()
  }

  const handleCustomize = () => {
    setIsOpen(true)
    setShowBanner(false)
  }

  const handleSavePreferences = () => {
    updateConsent(consentSettings)
    setIsOpen(false)
  }

  const handleToggleConsent = (category: keyof Omit<CookieConsent, 'consentDate' | 'necessary'>) => {
    setConsentSettings((prev: CookieConsent) => ({
      ...prev,
      [category]: !prev[category],
    }))
  }

  const openPreferences = () => {
    const savedConsent = getConsent()
    setConsentSettings(savedConsent)
    setIsOpen(true)
  }

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 w-full bg-white border-t border-navy p-4 z-50 shadow-lg">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2 text-navy">
                  Privolitev za piškotke
                </h3>
                <p className="text-sm text-navy max-w-3xl">
                  Ta spletna stran uporablja piškotke za izboljšanje uporabniške izkušnje.
                </p>
              </div>
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleRejectAll}
                  className="whitespace-nowrap"
                >
                  Zavrni vse
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleCustomize}
                  className="whitespace-nowrap"
                >
                  Prilagodi
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleAcceptAll}
                  className="whitespace-nowrap"
                >
                  Sprejmi vse
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/30 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-md shadow-lg z-50 w-[90vw] sm:max-w-[500px]">
            <Dialog.Title className="text-lg font-semibold text-navy">
              Nastavitve piškotkov
            </Dialog.Title>
            <Dialog.Description className="text-sm text-navy mt-1">
              Upravljajte z nastavitvami piškotkov.
            </Dialog.Description>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label
                    htmlFor="necessary-cookies"
                    className="font-medium text-navy"
                  >
                    Obvezni piškotki
                  </Label>
                  <p className="text-sm text-navy">
                    Ti piškotki so nujni za delovanje spletne strani.
                  </p>
                </div>
                <Switch id="necessary-cookies" checked={true} disabled={true} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label
                    htmlFor="preferences-cookies"
                    className="font-medium text-navy"
                  >
                    Piškotki za nastavitve
                  </Label>
                  <p className="text-sm text-navy">
                    Ti piškotki si zapomnijo vaše nastavitve.
                  </p>
                </div>
                <Switch
                  id="preferences-cookies"
                  checked={consentSettings.preferences}
                  onCheckedChange={() => handleToggleConsent("preferences")}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label
                    htmlFor="analytics-cookies"
                    className="font-medium text-navy"
                  >
                    Analitični piškotki
                  </Label>
                  <p className="text-sm text-navy">
                    Ti piškotki nam pomagajo izboljšati našo spletno stran.
                  </p>
                </div>
                <Switch
                  id="analytics-cookies"
                  checked={consentSettings.analytics}
                  onCheckedChange={() => handleToggleConsent("analytics")}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label
                    htmlFor="marketing-cookies"
                    className="font-medium text-navy"
                  >
                    Marketinški piškotki
                  </Label>
                  <p className="text-sm text-navy">
                    Ti piškotki se uporabljajo za marketinške namene.
                  </p>
                </div>
                <Switch
                  id="marketing-cookies"
                  checked={consentSettings.marketing}
                  onCheckedChange={() => handleToggleConsent("marketing")}
                />
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:space-x-2">
              <Dialog.Close asChild>
                <Button variant="secondary">Prekliči</Button>
              </Dialog.Close>
              <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
                <Button variant="secondary" onClick={handleRejectAll}>
                  Zavrni vse
                </Button>
                <Button variant="primary" onClick={handleSavePreferences}>
                  Shrani nastavitve
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

export default CookieConsentBanner 